import React, { useEffect, useState } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { postRequest } from "../../../../utils/httpRequest";
import { Priceable } from "../../Types";

interface Props {
  couponCode: string;
  priceable: Priceable;
  refCode: string;
  applyVoucher: (couponCode: string, refCode: string) => Promise<void>;
}

type FeedbackKind = "fail" | "success" | "chancer" | "";

const chancerCode = "CHANCER10";

const priceableType = (priceable: Priceable) => {
  if (priceable?.type === "course") return "Course";
  if (priceable?.type === "pass") return "Merchant::Plan";

  return "";
};

const Voucher: React.FC<Props> = ({
  couponCode,
  priceable,
  refCode,
  applyVoucher,
}) => {
  const [code, setCode] = useState(couponCode || refCode || "");
  const [feedback, setFeedback] = useState("");
  const [feedbackKind, setFeedbackKind] = useState<FeedbackKind>("");
  const [isExpanded, setIsExpanded] = useState(Boolean(couponCode || refCode));
  const [isFlashing, setIsFlashing] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voucherAttempts, setVoucherAttempts] = useState(0);
  const [chancerCodeShown, setChancerCodeShown] = useState(false);

  useEffect(() => {
    const appliedCode = couponCode || refCode;

    if (appliedCode) {
      setCode(appliedCode);
      setIsExpanded(true);
      setFeedbackKind("success");
      setFeedback(`${appliedCode} applied.`);
    } else {
      setCode("");
      setFeedback("");
      setFeedbackKind("");
      setIsFlashing(false);
      setIsShaking(false);
      setVoucherAttempts(0);
      setChancerCodeShown(false);
    }
  }, [couponCode, refCode]);

  const flashFeedback = () => {
    setIsFlashing(false);
    window.setTimeout(() => setIsFlashing(true), 0);
  };

  const shakeInput = () => {
    setIsShaking(false);
    window.setTimeout(() => setIsShaking(true), 0);
    window.setTimeout(() => setIsShaking(false), 500);
  };

  const showChancerCode = () => {
    setFeedbackKind("chancer");
    setChancerCodeShown(true);
    setFeedback("");
    flashFeedback();
  };

  const handleSubmit = (event?: React.FormEvent, voucherCode = code) => {
    event?.preventDefault();

    const nextCode = voucherCode.trim().toUpperCase();
    if (!nextCode || !priceable) return;

    setCode(nextCode);
    setSubmitting(true);

    return postRequest("/api/merchant/coupon_claimability.json", {
      code: nextCode,
      priceable_type: priceableType(priceable),
      priceable_id: priceable.id,
    })
      .then((response: AxiosResponse) => {
        if (response.data.status === "chancer") {
          showChancerCode();
          return;
        }

        if (response.data.claimable) {
          const nextCouponCode = response.data.referral ? "" : nextCode;
          const nextRefCode = response.data.referral ? nextCode : "";

          return applyVoucher(nextCouponCode, nextRefCode).then(() => {
            setFeedbackKind("success");
            setFeedback(
              nextCode === chancerCode
                ? `${chancerCode} applied: 10% off, your persistence has been rewarded.`
                : `${nextCode} applied.`,
            );
            flashFeedback();
          });
        }

        const nextAttempts = voucherAttempts + 1;
        setVoucherAttempts(nextAttempts);

        if (nextAttempts >= 4 && !chancerCodeShown) {
          showChancerCode();
          return;
        }

        setFeedbackKind("fail");
        setFeedback(
          response.data.error || "That code didn't work. Have another go.",
        );
        shakeInput();
        flashFeedback();
      })
      .catch((error: AxiosError) => {
        setFeedbackKind("fail");
        setFeedback(
          (error.response?.data as any)?.error ||
            "That code didn't work. Have another go.",
        );
        shakeInput();
        flashFeedback();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const applyChancerCode = () => {
    handleSubmit(undefined, chancerCode);
  };

  return (
    <>
      {!isExpanded && (
        <button
          className="voucher-toggle"
          type="button"
          onClick={() => setIsExpanded(true)}
        >
          Have a voucher code? +
        </button>
      )}
      <form
        className={`voucher-row${isExpanded ? " show" : ""}${
          isShaking ? " shake" : ""
        }`}
        onSubmit={handleSubmit}
      >
        <input
          type="text"
          placeholder="Enter voucher code"
          aria-label="Voucher code"
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <button type="submit" disabled={submitting}>
          Gimme
        </button>
      </form>
      <div
        className={`voucher-feedback ${feedbackKind}${
          isFlashing ? " flash" : ""
        }`.trim()}
        role="status"
        aria-live="polite"
      >
        {feedbackKind === "chancer" ? (
          <>
            <div className="chancer-line">
              Four attempts noted. You've either been very thorough or very
              hopeful. Either way, on the house: <code>{chancerCode}</code> for
              10% off.
            </div>
            <button
              className="chancer-apply"
              type="button"
              onClick={applyChancerCode}
            >
              Apply it
            </button>
          </>
        ) : (
          feedback
        )}
      </div>
    </>
  );
};

export default Voucher;
